/* The ledger — STORY-020.
 *
 * ── the totals come from the server, always ─────────────────────────────────
 * They describe the whole filtered set rather than the page, and they are never
 * recomputed here. A second implementation of a sum is a second implementation
 * that can disagree with the first, and on this screen the sums are the
 * product.
 *
 * ── one strip per currency ──────────────────────────────────────────────────
 * Money in different currencies does not add up. A single combined figure would
 * either be a lie or need a conversion rate this product does not have, so each
 * currency gets its own line — and income and expense stay apart, because "what
 * came in" and "what went out" are the two questions being asked.
 *
 * ── amounts are parsed once, at this boundary ───────────────────────────────
 * What somebody typed is in MAJOR units and their own separators; what the API
 * takes is an integer of MINOR units. `parseMoney` does that conversion in one
 * place, using the digit count of the chosen currency — never a constant two.
 */

import {
  type Category,
  EVENT_STATUSES,
  EVENTS_PAGE_MAX,
  TRANSACTION_DESCRIPTION_MAX,
  TRANSACTION_DIRECTIONS,
  TRANSACTIONS_PAGE_SIZE,
  type Transaction,
  type TransactionDirection,
} from '@eventuley/contracts';
import { amountOf, formatMoney, money, parseCurrency, parseMoney } from '@eventuley/domain';
import { formatDate, type MessageKey } from '@eventuley/i18n';
import { useParams } from '@tanstack/react-router';
import { useId, useState } from 'react';
import { ApiError, NetworkError } from '../../../shared/api/client.js';
import { useI18n, useTranslate } from '../../../shared/i18n/index.js';
import { Button, Callout, MoneyInput, Tabs, TextField } from '../../../shared/ui/index.js';
import { useEvents } from '../../events/index.js';
import { useEnabledValues, useOrganisation } from '../../organisations/index.js';
import {
  useCategories,
  useDeleteTransaction,
  useLedger,
  useRecordTransaction,
} from '../api/index.js';
import { CategoriesPanel } from '../components/categories.js';
import { Dashboard } from '../components/dashboard.js';
import { Reconciliation } from '../components/reconciliation.js';

/* Explicit, never a constructed key — `i18n.md` forbids one because it defeats
 * extraction and unused-key detection. */
const DIRECTION_LABELS = {
  income: 'money.direction.income',
  expense: 'money.direction.expense',
} as const satisfies Record<TransactionDirection, MessageKey>;

const EMPTY_DRAFT = {
  occurredOn: '',
  direction: 'expense' as TransactionDirection,
  amount: '',
  currency: '',
  categoryId: '',
  eventId: '',
  counterparty: '',
  description: '',
};

/**
 * Minor units to something a person reads, in their own locale.
 *
 * The digit count comes from the CURRENCY, never from a constant — JPY has
 * none and KWD has three. A code the runtime does not know is shown as the
 * bare number and the code rather than guessed at, because a wrong currency
 * symbol on a real figure is worse than no symbol.
 */
function useMoneyFormatter(locale: string) {
  return (minor: number, currency: string) => {
    const code = parseCurrency(currency);
    return code ? formatMoney(money(minor, code), locale) : `${minor} ${currency}`;
  };
}

/**
 * The Money destination — two views of the same thing.
 *
 * Tabs rather than two nav entries, following the prototype: entries and the
 * categories they are filed under are one subject, and a Member managing
 * categories is doing so because of what they just saw in the ledger.
 */
export function MoneyScreen() {
  const t = useTranslate();
  const { organisationId } = useParams({ strict: false }) as { organisationId: string };
  const [tab, setTab] = useState('ledger');

  return (
    <>
      <div className="page-head">
        <h1>{t('money.title')}</h1>
      </div>
      <Tabs
        label={t('money.tabs_label')}
        selected={tab}
        onSelect={setTab}
        tabs={[
          {
            /* First in the row but NOT the selected one, matching the
               prototype. Recording what a night cost is the most frequent
               thing anybody does here, and putting a dashboard in front of it
               costs a click every single time — a summary is read far less
               often than the ledger is written.

               A tab under Money rather than a ninth navigation destination —
               the prototype's placement, and its reasoning holds: STORY-039
               settled the navigation at eight. The known cost is that three of
               these tiles count Events rather than money. */
            id: 'dashboard',
            label: t('money.tab_dashboard'),
            panel: <Dashboard organisationId={organisationId} />,
          },
          {
            id: 'ledger',
            label: t('money.tab_ledger'),
            panel: <LedgerPanel organisationId={organisationId} />,
          },
          {
            /* Its own tab rather than a strip above the ledger. These figures
               describe the whole workspace and never the filtered page, and
               sitting them on top of a paged, filterable list is how somebody
               comes to read one as describing the other. */
            id: 'reconciliation',
            label: t('money.tab_reconciliation'),
            panel: <Reconciliation organisationId={organisationId} />,
          },
          {
            id: 'categories',
            label: t('money.tab_categories'),
            panel: <CategoriesPanel organisationId={organisationId} />,
          },
        ]}
      />
    </>
  );
}

function LedgerPanel({ organisationId }: { readonly organisationId: string }) {
  const t = useTranslate();
  const { formatLocale: locale } = useI18n();
  const format = useMoneyFormatter(locale);

  const [page, setPage] = useState(1);
  const [recording, setRecording] = useState(false);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [amountError, setAmountError] = useState<string | null>(null);

  const directionId = useId();
  const categoryId = useId();
  const eventId = useId();

  const query = {
    limit: TRANSACTIONS_PAGE_SIZE,
    offset: (page - 1) * TRANSACTIONS_PAGE_SIZE,
  };
  const { data, error, isLoading } = useLedger(organisationId, query);
  const { data: categoryData } = useCategories(organisationId);
  const { data: enabled } = useEnabledValues(organisationId);
  /* The workspace's OWN currency, which the story asks to be preselected.
   * The enabled list is not it: that endpoint has no ORDER BY, and a new
   * workspace is seeded with five defaults plus its own — so taking the first
   * one opened a Turkish workspace's form preset to USD. */
  const { data: workspace } = useOrganisation(organisationId);
  /* Recording what a night COST is the primary case, and by then it is
   * completed — so every status rather than the default three, which hide
   * completed and cancelled. Newest first and as many as the endpoint gives,
   * because the default page is 25 sorted by date ASCENDING: a workspace past
   * its first two dozen Events offered only its EARLIEST ones.
   *
   * Both defaults failed the same way. The one Event somebody wanted to
   * attribute to was the one not offered, and the money fell through to
   * Overhead with nothing on screen to say why. */
  const { data: eventData } = useEvents(organisationId, {
    status: [...EVENT_STATUSES],
    sort: 'date',
    direction: 'desc',
    limit: EVENTS_PAGE_MAX,
  });

  const record = useRecordTransaction(organisationId);

  if (error) {
    return (
      <Callout tone="danger">
        {error instanceof NetworkError ? t('error.network') : t('error.unexpected')}
      </Callout>
    );
  }

  const currencies = (enabled?.values ?? [])
    .filter((value) => value.kind === 'currency')
    .map((value) => value.value);

  const categories = categoryData?.categories ?? [];
  /* Only the ones matching the Direction. A Category from the other side is
   * refused by the server, and offering it here would be inviting the refusal
   * — the source workbook's one name on both sides is exactly this mistake. */
  const forDirection = categories.filter((category) => category.direction === draft.direction);

  /**
   * The currency the form starts on.
   *
   * Derived rather than snapshotted when the form opens: the workspace read may
   * not have landed by the time somebody clicks, and a value captured then
   * would keep the fallback for good. `draft.currency` wins as soon as there is
   * one, so an explicit choice is never overwritten by data arriving late.
   */
  const defaultCurrency = workspace?.currency ?? currencies[0] ?? '';
  const chosenCurrency = draft.currency || defaultCurrency;

  const transactions = data?.transactions ?? [];
  const totals = data?.totals ?? [];

  const firstOnPage = (page - 1) * TRANSACTIONS_PAGE_SIZE + 1;
  const lastPage = Math.max(1, Math.ceil((data?.total ?? 0) / TRANSACTIONS_PAGE_SIZE));

  /* Corrected during render rather than in an effect, and guarded on `data` so
   * an in-flight first load cannot drag a deliberate page back to one. The
   * guest list learned this the hard way: deleting the last entry on the last
   * page left the page number past the end of the list, with the controls that
   * would have got you back hidden at the same moment. */
  if (data && page > lastPage) setPage(lastPage);

  const reset = () => {
    setDraft(EMPTY_DRAFT);
    setAmountError(null);
    record.reset();
    setRecording(false);
  };

  const submit = () => {
    const code = parseCurrency(chosenCurrency);
    if (!code) {
      setAmountError(t('money.amount_invalid'));
      return;
    }

    /* Parsed ONCE, here, with the digit count of the chosen currency. JPY has
     * none and KWD has three; anything assuming two is a bug waiting for its
     * first customer in the wrong country. */
    const parsed = parseMoney(draft.amount, code, locale);
    if (!parsed.ok) {
      setAmountError(t('money.amount_invalid'));
      return;
    }

    const minor = amountOf(parsed.value);
    if (minor <= 0) {
      /* Zero and negative are both refused, and for different reasons worth
         keeping apart in the copy: the Direction carries the sign, and a
         zero-value row reads as a placeholder rather than a fact. */
      setAmountError(t('money.amount_above_zero'));
      return;
    }

    setAmountError(null);
    record.mutate(
      {
        occurredOn: draft.occurredOn,
        direction: draft.direction,
        amountMinor: minor,
        currency: code,
        categoryId: draft.categoryId,
        ...(draft.eventId === '' ? {} : { eventId: draft.eventId }),
        ...(draft.counterparty.trim() === '' ? {} : { counterparty: draft.counterparty.trim() }),
        description: draft.description,
      },
      {
        onSuccess: () => {
          /* Newest first, so a new entry is on page one — and staying on page
             four after recording would look as though nothing had happened. */
          setPage(1);
          reset();
        },
      },
    );
  };

  return (
    <>
      {/* The heading belongs to the screen, not to this panel — so the row
          here carries only the action. */}
      <div className="page-head">
        <span />
        {recording ? null : (
          <Button
            onClick={() => {
              setRecording(true);
              setDraft(EMPTY_DRAFT);
            }}
          >
            {t('money.record')}
          </Button>
        )}
      </div>

      {/* One line per currency, income and expense apart. Never a single net
          figure: "we took 4,000 and spent 3,600" and "we are 400 up" are
          different facts, and only the first survives being wrong. */}
      {totals.length > 0 ? (
        <ul className="row-list row-list--tight" aria-label={t('money.totals_label')}>
          {totals.map((total) => (
            <li className="row" key={total.currency}>
              <span className="row__main">
                <span className="row__title">{total.currency}</span>
              </span>
              <span className="row__actions">
                <span className="row__figure">
                  {t('money.amount_in', { amount: format(total.incomeMinor, total.currency) })}
                </span>
                <span className="row__figure">
                  {t('money.amount_out', { amount: format(total.expenseMinor, total.currency) })}
                </span>
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {recording ? (
        <section className="settings__section">
          <h2>{t('money.record_heading')}</h2>
          <form
            onSubmit={(submitted) => {
              submitted.preventDefault();
              submit();
            }}
            noValidate
          >
            <div className="field">
              <label className="field__label" htmlFor={directionId}>
                {t('money.direction_label')}
              </label>
              <select
                id={directionId}
                className="field__control"
                value={draft.direction}
                onChange={(changed) =>
                  setDraft({
                    ...draft,
                    direction: changed.target.value as TransactionDirection,
                    /* Cleared, because a Category from the other Direction is
                       refused — keeping it armed would produce a refusal on
                       save that the reader could not have predicted. */
                    categoryId: '',
                  })
                }
              >
                {TRANSACTION_DIRECTIONS.map((direction) => (
                  <option key={direction} value={direction}>
                    {t(DIRECTION_LABELS[direction])}
                  </option>
                ))}
              </select>
            </div>

            <MoneyInput
              label={t('money.amount_label')}
              currencyLabel={t('money.currency_label')}
              value={draft.amount}
              onValueChange={(next) => setDraft({ ...draft, amount: next })}
              currency={chosenCurrency}
              onCurrencyChange={(next) => setDraft({ ...draft, currency: next })}
              currencies={currencies}
              hint={t('money.currency_hint')}
              error={
                amountError ??
                (record.error instanceof ApiError &&
                fieldRule(record.error, 'currency') === 'not_enabled'
                  ? t('money.currency_not_enabled')
                  : undefined)
              }
            />

            <TextField
              label={t('money.date_label')}
              type="date"
              value={draft.occurredOn}
              onChange={(changed) => setDraft({ ...draft, occurredOn: changed.target.value })}
              hint={t('money.date_hint')}
            />

            <div className="field">
              <label className="field__label" htmlFor={categoryId}>
                {t('money.category_label')}
              </label>
              <select
                id={categoryId}
                className="field__control"
                value={draft.categoryId}
                onChange={(changed) => setDraft({ ...draft, categoryId: changed.target.value })}
              >
                <option value="">{t('money.category_none')}</option>
                {forDirection.map((category: Category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label className="field__label" htmlFor={eventId}>
                {t('money.event_label')}
              </label>
              <select
                id={eventId}
                className="field__control"
                value={draft.eventId}
                onChange={(changed) => setDraft({ ...draft, eventId: changed.target.value })}
              >
                {/* Overhead is the FIRST option and it is named, not blank. A
                    Transaction with no Event is a real and expected answer, and
                    an empty option reads as one somebody forgot to fill. */}
                <option value="">{t('money.event_overhead')}</option>
                {(eventData?.events ?? []).map((event) => (
                  <option key={event.id} value={event.id}>
                    {event.name}
                  </option>
                ))}
              </select>
              <p className="field__hint">
                {/* Said out loud when the list is not all of them. A picker
                    that quietly holds the newest hundred is right almost
                    always and wrong silently the once — and the reader would
                    conclude their Event had been deleted rather than that a
                    control had a limit. */}
                {(eventData?.total ?? 0) > (eventData?.events.length ?? 0)
                  ? t('money.event_hint_truncated', {
                      count: String(eventData?.events.length ?? 0),
                    })
                  : t('money.event_hint')}
              </p>
            </div>

            <TextField
              label={t('money.description_label')}
              value={draft.description}
              maxLength={TRANSACTION_DESCRIPTION_MAX}
              onChange={(changed) => setDraft({ ...draft, description: changed.target.value })}
            />

            <TextField
              label={t('money.counterparty_label')}
              value={draft.counterparty}
              onChange={(changed) => setDraft({ ...draft, counterparty: changed.target.value })}
              hint={t('money.counterparty_hint')}
            />

            {/* The two refusals with copy of their own are rendered beside
                the field they are about. Everything else falls through to the
                callout below — including a field refusal we have no specific
                wording for, which is the case that rendered NOTHING AT ALL:
                submitting the form as it opens is refused on three fields at
                once, and the button simply un-busied. A refusal nobody can see
                is indistinguishable from a screen that has stopped working. */}
            {fieldRule(record.error, 'categoryId') === 'direction_mismatch' ? (
              <Callout tone="danger">{t('money.category_wrong_direction')}</Callout>
            ) : record.error && !handledElsewhere(record.error) ? (
              <Callout tone="danger">
                {record.error instanceof NetworkError
                  ? t('error.network')
                  : record.error instanceof ApiError && record.error.code === 'validation_failed'
                    ? t('money.check_the_form')
                    : t('error.unexpected')}
              </Callout>
            ) : null}

            <div className="btn-row">
              <Button type="submit" busy={record.isPending}>
                {t('money.record_submit')}
              </Button>
              <Button variant="ghost" onClick={reset}>
                {t('money.record_cancel')}
              </Button>
            </div>
          </form>
        </section>
      ) : null}

      {isLoading ? <p className="field__hint">{t('common.loading')}</p> : null}

      {!isLoading && transactions.length === 0 ? (
        <Callout tone="info">
          <strong>{t('money.empty.title')}</strong>
          <p>{t('money.empty.body')}</p>
        </Callout>
      ) : null}

      {transactions.length > 0 ? (
        <ul className="row-list">
          {transactions.map((transaction) => (
            <LedgerRow
              key={transaction.id}
              transaction={transaction}
              organisationId={organisationId}
            />
          ))}
        </ul>
      ) : null}

      {data && data.total > 0 ? (
        <div className="filters">
          {/* Without these the ledger showed the first twenty-five entries and
              said so, and the rest were unreachable — the story defers the
              FILTER controls deliberately and said nothing about paging, which
              is how a screen came to hide money that had been recorded. */}
          <p className="field__hint" role="status">
            {t('money.showing_page', {
              from: firstOnPage,
              to: firstOnPage + transactions.length - 1,
              total: data.total,
              page,
              pages: lastPage,
            })}
          </p>

          {lastPage > 1 || page > 1 ? (
            <div className="btn-row">
              <Button variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                {t('money.previous')}
              </Button>
              <Button variant="ghost" disabled={page >= lastPage} onClick={() => setPage(page + 1)}>
                {t('money.next')}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

function LedgerRow({
  transaction,
  organisationId,
}: {
  transaction: Transaction;
  organisationId: string;
}) {
  const t = useTranslate();
  const { formatLocale: locale } = useI18n();
  const format = useMoneyFormatter(locale);
  const remove = useDeleteTransaction(organisationId);
  /* A question before the act. The row is hard-deleted server-side and the
   * trail entry keeps only who and when — by design, since a trail carrying
   * the amount would reconstruct the record it was meant to sever — so a
   * mis-click destroys a financial record with nothing to restore it from.
   * The same reason People's erasure asks first. */
  const [confirming, setConfirming] = useState(false);

  return (
    <li className="row">
      <span className="row__main">
        <span className="row__title">{transaction.description}</span>
        {/* Formatted, never printed raw — `i18n.md` hard rule 3. A Turkish
            reader sees "12 Eylül 2026", not "2026-09-12". Read in UTC because
            the value is a calendar DATE: parsing it as a local instant would
            shift it a day for anybody west of the meridian. */}
        <span className="row__meta">
          {t('money.row_meta', {
            date: formatDate(new Date(`${transaction.occurredOn}T00:00:00Z`), {
              locale,
              timeZone: 'UTC',
            }),
            category: transaction.category.name,
          })}
        </span>
        {/* Overhead is stated, never left blank. A row with no Event is a real
            answer and a blank reads as a missing one. */}
        <span className="row__meta">
          {transaction.event ? transaction.event.name : t('money.event_overhead')}
        </span>
        {transaction.counterparty ? (
          <span className="row__meta">{transaction.counterparty}</span>
        ) : null}
      </span>

      <span className="row__actions">
        {/* The Direction is said in words as well as carried by the sign —
            `design.md` forbids colour or a symbol alone. */}
        {/* One key carrying the amount, never a translated word glued to a
            formatted number — `i18n.md` forbids the concatenation, and Turkish
            attaches its suffixes to the word. The direction is said in words
            rather than by a sign or a colour. */}
        <span className="row__figure">
          {transaction.direction === 'income'
            ? t('money.amount_in', {
                amount: format(transaction.amountMinor, transaction.currency),
              })
            : t('money.amount_out', {
                amount: format(transaction.amountMinor, transaction.currency),
              })}
        </span>
        <Button variant="ghost" onClick={() => setConfirming(true)}>
          {t('money.delete')}
        </Button>
      </span>

      {confirming ? (
        <div className="row__expand">
          <Callout tone="danger">
            {/* Names WHAT is about to go, in the entry's own words and its own
                money. "Are you sure?" beside a row somebody has already
                stopped reading is a question nobody can answer. */}
            <strong>
              {t('money.delete_confirm_title', { description: transaction.description })}
            </strong>
            <p>{t('money.delete_confirm_body')}</p>
            <div className="btn-row">
              <Button
                variant="secondary"
                busy={remove.isPending}
                onClick={() => remove.mutate(transaction.id)}
              >
                {t('money.delete_confirm_submit')}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setConfirming(false);
                  remove.reset();
                }}
              >
                {t('money.delete_confirm_cancel')}
              </Button>
            </div>
          </Callout>
        </div>
      ) : null}

      {remove.error ? (
        <div className="row__expand">
          <Callout tone="danger">
            {remove.error instanceof NetworkError ? t('error.network') : t('money.delete_failed')}
          </Callout>
        </div>
      ) : null}
    </li>
  );
}

/**
 * True when a refusal already has wording of its own somewhere on the form.
 *
 * Deliberately a list of the two that do, rather than "any field refusal": the
 * previous version suppressed the general message for EVERY field-level 400,
 * so a refusal we had not written copy for showed nothing anywhere.
 */
function handledElsewhere(error: unknown): boolean {
  return (
    fieldRule(error, 'currency') === 'not_enabled' ||
    fieldRule(error, 'categoryId') === 'direction_mismatch'
  );
}

/** The rule the server gave for one field, or undefined. */
function fieldRule(error: unknown, path: string): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  return error.fields?.find((field) => field.path === path)?.rule;
}
